import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Sparkles, Check } from "lucide-react";
import { useReveal } from "@/lib/animations";
import { BUSINESS_STRATEGIES } from "@/lib/data";
import { Aurora } from "@/components/Aurora";

export default function Business() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <main ref={ref} data-testid="business-page" className="bg-[#F9F6F0]">
      {/* Hero */}
      <section className="relative bg-[#F0EBE0] grain overflow-hidden">
        <Aurora className="z-0" intensity="subtle" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="w-16 h-16 rounded-2xl bg-breeze-gradient text-white flex items-center justify-center shadow-lg shadow-[#29ABE2]/25 mb-8">
                <Briefcase size={28} />
              </div>
              <p
                data-reveal
                className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4"
              >
                For business owners
              </p>
              <h1
                data-reveal
                className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.92] text-[#0B132B]"
              >
                Strategies that protect{" "}
                <span className="text-gradient-breeze">your business</span> like it's family.
              </h1>
              <p
                data-reveal
                className="mt-7 text-lg text-[#334155] font-light leading-relaxed max-w-2xl"
              >
                The same tax code that quietly builds wealth for the largest
                corporations is available to closely held businesses too — if you
                know which structures to use. We help business owners deploy four
                strategies that retain talent, protect operations, and create
                tax-advantaged wealth.
              </p>
              <div data-reveal className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/quote?product=Business%20Owner%20Strategy"
                  data-testid="business-hero-quote-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
                >
                  Talk to a strategist <ArrowRight size={17} />
                </Link>
                <a
                  href="#strategies"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] font-semibold px-8 py-4 border border-[#0B132B]/10 transition-all duration-300 hover:border-[#29ABE2] hover:text-[#29ABE2]"
                >
                  Explore the four strategies
                </a>
              </div>
            </div>

            <div data-reveal className="lg:col-span-5 relative lg:sticky lg:top-28">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-navy-deep-gradient opacity-30 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-[2rem] overflow-hidden bg-[#0B132B] text-white shadow-2xl shadow-[#0B132B]/30 border border-white/10">
                <div className="relative bg-navy-deep-gradient p-8 md:p-10">
                  <Aurora intensity="intense" />
                  <div className="relative z-10">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-bold">
                      <Sparkles size={13} /> The Playbook
                    </span>
                    <h3 className="mt-7 font-heading font-black text-3xl tracking-tight leading-tight">
                      Four strategies the largest firms use — adapted for closely held businesses.
                    </h3>
                  </div>
                </div>
                <div className="p-8 md:p-10 space-y-4">
                  {BUSINESS_STRATEGIES.map((s) => (
                    <Link
                      key={s.id}
                      to={`/business/${s.id}`}
                      className="flex items-center justify-between gap-4 group/r"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Check size={16} className="text-[#29ABE2] shrink-0" />
                        <span className="text-sm font-medium text-white/90 truncate group-hover/r:text-[#29ABE2] transition-colors">
                          {s.name}
                        </span>
                      </div>
                      <span className="text-[10px] tracking-[0.22em] uppercase text-white/40 font-semibold shrink-0">
                        {s.tag}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro educational belt */}
      <section className="bg-white border-y border-[#0B132B]/8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-24 grid grid-cols-1 md:grid-cols-3 md:divide-x divide-[#0B132B]/8 gap-12 md:gap-0">
          {[
            {
              k: "Protect",
              v: "Insure the people and ownership structures the business literally can't run without.",
            },
            {
              k: "Retain",
              v: "Reward and lock in the executives you need to keep — with benefits competitors don't offer.",
            },
            {
              k: "Transfer",
              v: "Plan the orderly transfer of ownership before death, disability, or retirement forces a fire sale.",
            },
          ].map((c, i) => (
            <div
              key={i}
              data-reveal
              data-reveal-delay={i * 0.08}
              className="px-0 md:px-10 first:md:pl-0 last:md:pr-0"
            >
              <p className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold">
                {c.k}
              </p>
              <p className="mt-4 text-[#334155] font-light leading-relaxed">{c.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Strategies list */}
      <section id="strategies" className="scroll-mt-28 max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-28">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          Four strategies
        </p>
        <h2
          data-reveal
          className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0B132B] max-w-3xl"
        >
          The institutional playbook,{" "}
          <span className="text-gradient-breeze">for your business</span>.
        </h2>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
          {BUSINESS_STRATEGIES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={s.id}
                to={`/business/${s.id}`}
                data-reveal
                data-reveal-delay={i * 0.07}
                data-testid={`business-strategy-card-${s.id}`}
                className="group rounded-3xl bg-white border border-[#0B132B]/5 shadow-sm p-8 md:p-10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:border-[#29ABE2]/30 flex flex-col"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center mb-6">
                  <Icon size={22} />
                </div>
                <p className="text-xs tracking-[0.22em] uppercase text-[#29ABE2] font-semibold mb-2">
                  {s.tag}
                </p>
                <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#0B132B]">
                  {s.name}
                </h3>
                <p className="mt-4 text-[#334155] font-light leading-relaxed">
                  {s.summary}
                </p>
                <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a6fc2]">
                  Read the full strategy
                  <ArrowRight
                    size={15}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B132B] text-white grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none max-w-3xl mx-auto"
          >
            Bring your business to the table.
          </h2>
          <p data-reveal className="mt-5 text-white/60 font-light max-w-md mx-auto">
            One conversation. Real numbers. We'll show you which of these four
            strategies actually fits your business — and which ones don't.
          </p>
          <div data-reveal className="mt-10">
            <Link
              to="/quote?product=Business%20Owner%20Strategy"
              data-testid="business-cta-quote-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#29ABE2] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-white hover:text-[#0B132B] hover:scale-105"
            >
              Book a strategy call <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
